import { type EditorView } from 'prosemirror-view'
import { Node as ProseNode } from 'prosemirror-model'
import { TextSelection } from 'prosemirror-state'

import { getNextTextPosition } from 'common/Prose/utils'

/**
 * Inserts a node at the current cursor position and focuses on it
 * @param view An instance of ProseView
 * @param node The node to insert
 */
export const insertAndFocus = (view: EditorView, node: ProseNode) => {
  const { state } = view
  const { $from, empty } = state.selection
  let insertPoint: number

  const tr = state.tr

  // NOTE: if we insert a list into a list item, ALWAYS insert after the list item
  if (
    empty &&
    $from.depth &&
    $from.depth - 1 &&
    $from.node($from.depth - 1).type === view.state.schema.nodes.list_item &&
    node.type.groups.includes('list')
  ) {
    insertPoint = $from.after($from.depth - 1)

    // NOTE: if we insert another list of the same type, merge them together
    tr.insert(insertPoint, node.type === $from.node($from.depth - 2).type ? node.content : node)

    tr.setSelection(new TextSelection(getNextTextPosition(tr.doc.resolve(insertPoint))))
  } else if (empty && $from.depth && !$from.node().childCount) {
    insertPoint = $from.before()

    // NOTE: if the node at the selection start is empty, replace it
    //       but don't delete the last paragraph of the doc, it will be recreated by PM and that messes up the tracked changes
    const nextNode = state.doc.nodeAt(insertPoint)
    const nextNodeIsLastP = state.doc.content.lastChild === nextNode && nextNode?.type === state.schema.nodes.paragraph
    if (!nextNodeIsLastP) {
      tr.delete(insertPoint, $from.after())
    }
    tr.insert(insertPoint, node)

    tr.setSelection(new TextSelection(getNextTextPosition(tr.doc.resolve(insertPoint))))
  } else if (empty && $from.depth) {
    insertPoint = $from.after()

    // NOTE: :if the selection is empty, insert after the node
    tr.insert(insertPoint, node)

    tr.setSelection(new TextSelection(getNextTextPosition(tr.doc.resolve(insertPoint))))
  } else {
    insertPoint =
      tr.selection.from === tr.selection.$from.start() && tr.selection.$from.node() !== state.doc
        ? tr.selection.$from.before()
        : tr.selection.from

    // Note: if it's not empty, remove the selection and insert it there
    tr.deleteSelection()
    tr.insert(insertPoint, node)

    tr.setSelection(new TextSelection(getNextTextPosition(tr.doc.resolve(insertPoint))))
  }

  tr.scrollIntoView()

  view.dispatch(tr)
  view.focus()

  return tr.doc.resolve(insertPoint)
}
